/**
 * WebhooksTab Component
 *
 * Self-contained component for managing webhook subscriptions within the
 * Auth Portal Account page. Uses Cloudscape Design System components exclusively.
 *
 * Communicates with the Webhooks API via webhooksService (which uses Platform_Core's
 * createApiClient for automatic token injection) and uses the AuthContext
 * for permission checks.
 *
 * Requirements: 14.1–14.15
 */

import React, { useCallback, useEffect, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Flashbar, { type FlashbarProps } from '@cloudscape-design/components/flashbar';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import { Modal, ConfirmDialog } from '@internal/platform-core';
import Multiselect, { type MultiselectProps } from '@cloudscape-design/components/multiselect';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import { useAuth } from '../contexts/AuthContext';
import {
  webhooksService,
  type WebhookSubscription,
  type WebhookDelivery,
} from '../services/webhooks.service';

// ---------------------------------------------------------------------------
// Client-side copy of EVENT_PERMISSION_MAP
// Canonical source: apis/core/src/webhooks/permissions.ts
// ---------------------------------------------------------------------------
const EVENT_PERMISSION_MAP: Record<string, string> = {
  task: 'tasks:read',
  project: 'projects:read',
  user: 'users:read',
  profile: 'profiles:read',
  attachment: 'attachments:read',
  notification: 'notifications:read',
  'work-order': 'work-orders:read',
  'time-entry': 'time-tracking:read',
  discussion: 'discussions:read',
  setting: 'settings:read',
  company: 'crm:read',
  contact: 'crm:read',
  lead: 'crm:read',
  activity: 'crm:read',
  relationship: 'crm:read',
  article: 'kb:read',
  category: 'kb:read',
  review: 'kb:read',
  rating: 'kb:read',
  site: 'kb:read',
  translation: 'kb:read',
  ticket: 'tickets:read',
  goal: 'goals:read',
  portfolio: 'portfolios:read',
  team: 'teams:read',
  department: 'departments:read',
  group: 'groups:read',
  role: 'roles:read',
  workflow: 'workflows:read',
  tag: 'tags:read',
  template: 'templates:read',
  view: 'views:read',
};

/**
 * Returns event type prefixes the user is permitted to subscribe to,
 * based on their permissions and the EVENT_PERMISSION_MAP.
 */
export function getPermittedEventPrefixes(userPermissions: string[]): string[] {
  if (
    userPermissions.includes('*') ||
    userPermissions.includes('webhooks:admin')
  ) {
    return Object.keys(EVENT_PERMISSION_MAP);
  }
  return Object.entries(EVENT_PERMISSION_MAP)
    .filter(([, requiredPerm]) => userPermissions.includes(requiredPerm))
    .map(([prefix]) => prefix);
}

// ---------------------------------------------------------------------------
// Helper: check a single permission (supports wildcard & webhooks:admin)
// ---------------------------------------------------------------------------
function hasPermission(
  permissions: string[] | undefined,
  required: string,
): boolean {
  if (!permissions) return false;
  return (
    permissions.includes(required) ||
    permissions.includes('*') ||
    permissions.includes('webhooks:admin')
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const WebhooksTab: React.FC = () => {
  const { activeProfile } = useAuth();

  // ---- state ----
  const [subscriptions, setSubscriptions] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSecretModal, setShowSecretModal] = useState<{
    visible: boolean;
    secret: string;
  }>({ visible: false, secret: '' });
  const [showDeliveryModal, setShowDeliveryModal] = useState<{
    visible: boolean;
    subscriptionId: string;
    deliveries: WebhookDelivery[];
  }>({ visible: false, subscriptionId: '', deliveries: [] });
  const [showDeleteModal, setShowDeleteModal] = useState<{
    visible: boolean;
    subscriptionId: string;
  }>({ visible: false, subscriptionId: '' });

  // Create-form state
  const [createTargetUrl, setCreateTargetUrl] = useState('');
  const [createEventTypes, setCreateEventTypes] = useState<
    MultiselectProps.Option[]
  >([]);
  const [createDescription, setCreateDescription] = useState('');
  const [createHeaderKey, setCreateHeaderKey] = useState('');
  const [createHeaderValue, setCreateHeaderValue] = useState('');
  const [createHeaders, setCreateHeaders] = useState<
    { key: string; value: string }[]
  >([]);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Delivery loading
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  // ---- permission checks ----
  const canCreate = hasPermission(
    activeProfile?.permissions,
    'webhooks:create',
  );

  // ---- data loading ----
  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await webhooksService.listSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
      setFlashItems([
        {
          type: 'error',
          header: 'Failed to load subscriptions',
          content:
            'An error occurred while loading your webhook subscriptions. Please try again.',
          dismissible: true,
          onDismiss: () => setFlashItems([]),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSubscriptions();
  }, [loadSubscriptions]);

  // ---- actions ----
  const handleCreateSubscription = async () => {
    setCreateSubmitting(true);
    try {
      const customHeaders: Record<string, string> | undefined =
        createHeaders.length > 0
          ? createHeaders.reduce<Record<string, string>>((acc, h) => {
              if (h.key.trim()) acc[h.key.trim()] = h.value;
              return acc;
            }, {})
          : undefined;

      const result = await webhooksService.createSubscription(
        {
          targetUrl: createTargetUrl,
          eventTypes: createEventTypes.map((o) => String(o.value)),
          description: createDescription || undefined,
          customHeaders,
        },
      );

      // Close create modal, open secret modal
      setShowCreateModal(false);
      resetCreateForm();
      setShowSecretModal({ visible: true, secret: result.secret });
      await loadSubscriptions();
    } catch (err) {
      console.error('Failed to create subscription:', err);
      setFlashItems([
        {
          type: 'error',
          header: 'Failed to create subscription',
          content:
            'An error occurred while creating the subscription. Please try again.',
          dismissible: true,
          onDismiss: () => setFlashItems([]),
        },
      ]);
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleToggleStatus = async (sub: WebhookSubscription) => {
    const newStatus = sub.status === 'active' ? 'paused' : 'active';
    try {
      await webhooksService.updateSubscription(
        sub.subscriptionId,
        { status: newStatus as 'active' | 'paused' },
      );
      await loadSubscriptions();
    } catch (err) {
      console.error('Failed to update subscription:', err);
      setFlashItems([
        {
          type: 'error',
          header: 'Failed to update subscription',
          content:
            'An error occurred while updating the subscription status. Please try again.',
          dismissible: true,
          onDismiss: () => setFlashItems([]),
        },
      ]);
    }
  };

  const handleRotateSecret = async (subscriptionId: string) => {
    try {
      const result = await webhooksService.rotateSecret(
        subscriptionId,
      );
      setShowSecretModal({ visible: true, secret: result.secret });
    } catch (err) {
      console.error('Failed to rotate secret:', err);
      setFlashItems([
        {
          type: 'error',
          header: 'Failed to rotate secret',
          content:
            'An error occurred while rotating the signing secret. Please try again.',
          dismissible: true,
          onDismiss: () => setFlashItems([]),
        },
      ]);
    }
  };

  const handleViewDeliveries = async (subscriptionId: string) => {
    setDeliveriesLoading(true);
    setShowDeliveryModal({
      visible: true,
      subscriptionId,
      deliveries: [],
    });
    try {
      const deliveries = await webhooksService.listDeliveries(
        subscriptionId,
      );
      setShowDeliveryModal({ visible: true, subscriptionId, deliveries });
    } catch (err) {
      console.error('Failed to load delivery history:', err);
      setFlashItems([
        {
          type: 'error',
          header: 'Failed to load delivery history',
          content:
            'An error occurred while loading delivery history. Please try again.',
          dismissible: true,
          onDismiss: () => setFlashItems([]),
        },
      ]);
      setShowDeliveryModal({ visible: false, subscriptionId: '', deliveries: [] });
    } finally {
      setDeliveriesLoading(false);
    }
  };

  const handleDeleteSubscription = async () => {
    if (!showDeleteModal.subscriptionId) return;
    try {
      await webhooksService.deleteSubscription(
        showDeleteModal.subscriptionId,
      );
      setShowDeleteModal({ visible: false, subscriptionId: '' });
      await loadSubscriptions();
    } catch (err) {
      console.error('Failed to delete subscription:', err);
      setFlashItems([
        {
          type: 'error',
          header: 'Failed to delete subscription',
          content:
            'An error occurred while deleting the subscription. Please try again.',
          dismissible: true,
          onDismiss: () => setFlashItems([]),
        },
      ]);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(showSecretModal.secret);
    } catch {
      // Fallback: select text for manual copy
    }
  };

  const resetCreateForm = () => {
    setCreateTargetUrl('');
    setCreateEventTypes([]);
    setCreateDescription('');
    setCreateHeaderKey('');
    setCreateHeaderValue('');
    setCreateHeaders([]);
  };

  const addCustomHeader = () => {
    if (createHeaderKey.trim()) {
      setCreateHeaders((prev) => [
        ...prev,
        { key: createHeaderKey.trim(), value: createHeaderValue },
      ]);
      setCreateHeaderKey('');
      setCreateHeaderValue('');
    }
  };

  const removeCustomHeader = (index: number) => {
    setCreateHeaders((prev) => prev.filter((_, i) => i !== index));
  };

  // ---- build event type options from permissions ----
  const permittedPrefixes = getPermittedEventPrefixes(
    activeProfile?.permissions ?? [],
  );
  const eventTypeOptions: MultiselectProps.Option[] = permittedPrefixes.map(
    (prefix) => ({
      label: prefix,
      value: prefix,
    }),
  );

  // ---- status indicator helper ----
  const renderStatus = (status: WebhookSubscription['status']) => {
    switch (status) {
      case 'active':
        return <StatusIndicator type="success">Active</StatusIndicator>;
      case 'paused':
        return <StatusIndicator type="stopped">Paused</StatusIndicator>;
      case 'disabled':
        return <StatusIndicator type="error">Disabled</StatusIndicator>;
      default:
        return <StatusIndicator type="info">{status}</StatusIndicator>;
    }
  };

  // ---- validate create form ----
  const isCreateFormValid =
    createTargetUrl.startsWith('https://') && createEventTypes.length > 0;

  // ---- render ----
  return (
    <SpaceBetween size="l">
      <Flashbar items={flashItems} />

      {/* Subscriptions Table */}
      <Table
        header={
          <Header
            actions={
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                disabled={!canCreate}
              >
                Add Subscription
              </Button>
            }
          >
            Webhook Subscriptions
          </Header>
        }
        loading={loading}
        loadingText="Loading subscriptions..."
        items={subscriptions}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No subscriptions</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              You have no webhook subscriptions yet.
            </Box>
          </Box>
        }
        columnDefinitions={[
          {
            id: 'subscriptionId',
            header: 'Subscription ID',
            cell: (item) => item.subscriptionId,
            sortingField: 'subscriptionId',
          },
          {
            id: 'targetUrl',
            header: 'Target URL',
            cell: (item) => item.targetUrl,
            sortingField: 'targetUrl',
          },
          {
            id: 'eventTypes',
            header: 'Event Types',
            cell: (item) => item.eventTypes.join(', '),
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item) => renderStatus(item.status),
            sortingField: 'status',
          },
          {
            id: 'createdAt',
            header: 'Created',
            cell: (item) =>
              new Date(item.createdAt).toLocaleDateString(),
            sortingField: 'createdAt',
          },
          {
            id: 'description',
            header: 'Description',
            cell: (item) => item.description || '—',
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="inline-link"
                  onClick={() => handleToggleStatus(item)}
                  disabled={item.status === 'disabled'}
                >
                  {item.status === 'active' ? 'Pause' : 'Resume'}
                </Button>
                <Button
                  variant="inline-link"
                  onClick={() => handleRotateSecret(item.subscriptionId)}
                >
                  Rotate Secret
                </Button>
                <Button
                  variant="inline-link"
                  onClick={() => handleViewDeliveries(item.subscriptionId)}
                >
                  View Deliveries
                </Button>
                <Button
                  variant="inline-link"
                  onClick={() =>
                    setShowDeleteModal({
                      visible: true,
                      subscriptionId: item.subscriptionId,
                    })
                  }
                >
                  Delete
                </Button>
              </SpaceBetween>
            ),
          },
        ]}
      />

      {/* Create Subscription Modal */}
      <Modal
        visible={showCreateModal}
        onDismiss={() => {
          setShowCreateModal(false);
          resetCreateForm();
        }}
        header="Create Webhook Subscription"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={() => {
                setShowCreateModal(false);
                resetCreateForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateSubscription}
              disabled={!isCreateFormValid}
              loading={createSubmitting}
            >
              Create
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          <FormField
            label="Target URL"
            description="The HTTPS endpoint that will receive webhook events."
            constraintText="Must start with https://"
          >
            <Input
              value={createTargetUrl}
              onChange={({ detail }) => setCreateTargetUrl(detail.value)}
              placeholder="https://example.com/webhooks"
              type="url"
            />
          </FormField>

          <FormField
            label="Event Types"
            description="Select the event type prefixes you want to subscribe to."
          >
            <Multiselect
              selectedOptions={createEventTypes}
              onChange={({ detail }) =>
                setCreateEventTypes(
                  detail.selectedOptions as MultiselectProps.Option[],
                )
              }
              options={eventTypeOptions}
              placeholder="Select event types"
              filteringType="auto"
            />
          </FormField>

          <FormField label="Description" description="Optional description for this subscription.">
            <Input
              value={createDescription}
              onChange={({ detail }) => setCreateDescription(detail.value)}
              placeholder="My integration webhook"
            />
          </FormField>

          <FormField
            label="Custom Headers (optional)"
            description="Add custom HTTP headers to include with each delivery."
          >
            <SpaceBetween size="s">
              <SpaceBetween direction="horizontal" size="xs">
                <Input
                  value={createHeaderKey}
                  onChange={({ detail }) => setCreateHeaderKey(detail.value)}
                  placeholder="Header name"
                />
                <Input
                  value={createHeaderValue}
                  onChange={({ detail }) => setCreateHeaderValue(detail.value)}
                  placeholder="Header value"
                />
                <Button onClick={addCustomHeader} disabled={!createHeaderKey.trim()}>
                  Add
                </Button>
              </SpaceBetween>
              {createHeaders.map((h, i) => (
                <SpaceBetween key={i} direction="horizontal" size="xs">
                  <Box>
                    <strong>{h.key}</strong>: {h.value}
                  </Box>
                  <Button
                    variant="inline-link"
                    onClick={() => removeCustomHeader(i)}
                  >
                    Remove
                  </Button>
                </SpaceBetween>
              ))}
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Secret Display Modal */}
      <Modal
        visible={showSecretModal.visible}
        onDismiss={() => setShowSecretModal({ visible: false, secret: '' })}
        header="Webhook Signing Secret"
        footer={
          <Button
            variant="primary"
            onClick={() => setShowSecretModal({ visible: false, secret: '' })}
          >
            Done
          </Button>
        }
      >
        <SpaceBetween size="m">
          <StatusIndicator type="warning">
            This secret will not be shown again. Copy it now.
          </StatusIndicator>
          <FormField label="HMAC Signing Secret">
            <SpaceBetween direction="horizontal" size="xs">
              <Input
                value={showSecretModal.secret}
                readOnly
                type="text"
              />
              <Button iconName="copy" onClick={handleCopySecret}>
                Copy
              </Button>
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Delivery History Modal */}
      <Modal
        visible={showDeliveryModal.visible}
        onDismiss={() =>
          setShowDeliveryModal({
            visible: false,
            subscriptionId: '',
            deliveries: [],
          })
        }
        header="Delivery History"
        size="large"
        footer={
          <Button
            variant="primary"
            onClick={() =>
              setShowDeliveryModal({
                visible: false,
                subscriptionId: '',
                deliveries: [],
              })
            }
          >
            Close
          </Button>
        }
      >
        <Table
          loading={deliveriesLoading}
          loadingText="Loading deliveries..."
          items={showDeliveryModal.deliveries}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No deliveries</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No delivery attempts found for this subscription.
              </Box>
            </Box>
          }
          columnDefinitions={[
            {
              id: 'timestamp',
              header: 'Timestamp',
              cell: (item) =>
                new Date(item.timestamp).toLocaleString(),
              sortingField: 'timestamp',
            },
            {
              id: 'eventType',
              header: 'Event Type',
              cell: (item) => item.eventType,
              sortingField: 'eventType',
            },
            {
              id: 'httpStatusCode',
              header: 'HTTP Status',
              cell: (item) =>
                item.httpStatusCode != null
                  ? String(item.httpStatusCode)
                  : '—',
            },
            {
              id: 'result',
              header: 'Result',
              cell: (item) =>
                item.success ? (
                  <StatusIndicator type="success">Success</StatusIndicator>
                ) : (
                  <StatusIndicator type="error">Failed</StatusIndicator>
                ),
            },
          ]}
        />
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        visible={showDeleteModal.visible}
        onCancel={() =>
          setShowDeleteModal({ visible: false, subscriptionId: '' })
        }
        onConfirm={handleDeleteSubscription}
        title="Delete Subscription"
        message="Are you sure you want to delete this subscription? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />
    </SpaceBetween>
  );
};
